import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Typed answers on the printed forms have to survive a photocopy and a clerk
 * reading them at arm's length, so they print larger and heavier than the
 * form's own text.
 *
 * Kalpurush ships a single weight, so `font-weight: 700` is only the browser's
 * synthetic bold. A sideways hairline shadow in the text colour thickens the
 * stems a touch further without filling in the Bangla counters, and
 * html2canvas renders text-shadow, so the exported PDF gets the same ink as
 * the preview.
 */
export const VALUE_INK_SHADOW = [
    '0.1px 0 0 #111',
    '-0.1px 0 0 #111',
].join(', ');

/**
 * Kalpurush's own metrics, read off the browser (`fontBoundingBox*`): the
 * ascent is 1 em and the descent 0.42 em.
 */
export const FONT_ASCENT = 1;
export const FONT_DESCENT = 0.42;

/** Answers get a 1.5 em line box, which leaves room for their descenders. */
export const VALUE_LINE_BOX = 1.5;

/**
 * Where CSS puts a baseline inside a line box: the leading is split above and
 * below the em box, so the baseline sits half a leading plus one ascent down.
 */
export const baselineOffset = (size, lineHeight) => (
    (lineHeight - (FONT_ASCENT + FONT_DESCENT) * size) / 2 + FONT_ASCENT * size
);

/**
 * html2canvas paints a baseline at the **bottom of the line box** instead, so
 * every line in normal flow lands `lineHeight - baselineOffset` too low. It is
 * nudged back with a paint-only `position: relative`, which leaves the layout —
 * and so the dotted lines — untouched.
 */
export const exportBaselineShift = (size, lineHeight) => (
    baselineOffset(size, lineHeight) - lineHeight
);

/**
 * Absolutely placed text is corrected by shrinking its line box to the baseline
 * offset instead, and picks up a further ~2.5 pt of drift on top — measured
 * against the preview's own baselines on the exported bitmap, and the same
 * number the Form-13 export uses.
 */
export const EXPORT_ABSOLUTE_DRIFT_PT = 2.5;

/**
 * Fit an answer that is allowed to wrap into a fixed number of lines.
 *
 * Same idea as `useFitFontSize`, measured by height instead of width: the value
 * starts at `size` and steps back toward `minSize` only while it needs more than
 * `lines` of them. For a blank too narrow to hold an address on one line at any
 * readable size.
 *
 * @param {number} size Preferred font size in px.
 * @param {number} minSize Smallest size to fall back to.
 * @param {number} lines How many lines the answer may take.
 * @param {number} lineHeight Line height as a multiple of the font size.
 * @param {Array} deps Re-measure when these change.
 * @returns {[import('react').RefObject<HTMLElement>, number]} ref and fitted size.
 */
export function useFitFontSizeToLines(size, minSize, lines, lineHeight, deps) {
    const ref = useRef(null);
    const [fitted, setFitted] = useState(size);

    useLayoutEffect(() => {
        if (!ref.current) return undefined;

        const fit = () => {
            const el = ref.current;
            if (!el) return;
            let next = size;
            el.style.fontSize = `${next}px`;
            const overflows = () => el.scrollHeight > next * lineHeight * lines + 0.5;
            while (next > minSize && overflows()) {
                next = Math.max(minSize, next - 0.3);
                el.style.fontSize = `${next}px`;
            }
            setFitted(next);
        };

        fit();
        // Kalpurush arrives late; see `useFitFontSize` for why this measures again.
        let cancelled = false;
        if (typeof document !== 'undefined' && document.fonts) {
            document.fonts.ready.then(() => { if (!cancelled) fit(); });
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return [ref, fitted];
}

/**
 * Fit a one-line answer to the blank it prints in.
 *
 * The value starts at `size` and steps back toward `minSize` — the form's own
 * body size — only while it overflows, so short answers keep the big face and
 * long ones (a migration reason, a full address) stay whole instead of being
 * cut off by the ellipsis.
 *
 * @param {number} size Preferred font size in px.
 * @param {number} minSize Smallest size to fall back to.
 * @param {Array} deps Re-measure when these change (the text and its box).
 * @returns {[import('react').RefObject<HTMLElement>, number]} ref and fitted size.
 */
export function useFitFontSize(size, minSize, deps) {
    const ref = useRef(null);
    const [fitted, setFitted] = useState(size);

    useLayoutEffect(() => {
        if (!ref.current) return undefined;

        const fit = () => {
            const el = ref.current;
            if (!el) return;
            let next = size;
            el.style.fontSize = `${next}px`;
            // Sub-pixel steps: one step is never visible, and the loop is bounded
            // by the gap between the two sizes.
            while (next > minSize && el.scrollWidth > el.clientWidth + 0.5) {
                next = Math.max(minSize, next - 0.3);
                el.style.fontSize = `${next}px`;
            }
            setFitted(next);
        };

        fit();

        // The forms are set in Kalpurush, which loads asynchronously. Measuring
        // before it arrives fits the answer to whatever fallback face is up —
        // wider, so the answer shrinks — and nothing would measure it again,
        // leaving it small for the life of the page. Re-fit once the real
        // metrics are in.
        let cancelled = false;
        if (typeof document !== 'undefined' && document.fonts) {
            document.fonts.ready.then(() => {
                if (!cancelled) fit();
            });
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return [ref, fitted];
}
